import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tag, Button, Modal, Form, Input, InputNumber, Select, Alert, Spin, Space, Empty, Popconfirm, Table, message, Tooltip } from 'antd';
import { PlusOutlined, StopOutlined, KeyOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import adminApi, { TenantInviteCode } from '../../services/tenantService';
import { SubscriptionPlan } from '../../types/tenant';

const { TextArea } = Input;

const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const statusColors: Record<string, string> = {
  active: 'green',
  used: 'blue',
  expired: 'default',
  revoked: 'red',
};

const AdminTenantInviteCodesPage: React.FC = () => {
  const [codes, setCodes] = useState<TenantInviteCode[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const [codeResult, planList] = await Promise.all([
        adminApi.listTenantInviteCodes({ limit: 100 }),
        adminApi.getPlans(),
      ]);
      setCodes(codeResult.data);
      setTotal(codeResult.total);
      setPlans(planList);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ expiresInHours: 168 });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await adminApi.createTenantInviteCode({
        expiresInHours: values.expiresInHours,
        note: values.note || undefined,
        intendedCompanyName: values.intendedCompanyName || undefined,
        intendedContactEmail: values.intendedContactEmail || undefined,
        intendedPlanId: values.intendedPlanId || undefined,
      });
      setModalOpen(false);
      fetchCodes();
    } catch (err) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      setError((err as Error).message);
    }
  };

  const handleRevoke = async (id: number) => {
    try {
      await adminApi.revokeTenantInviteCode(id);
      fetchCodes();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => message.success(`Copied ${code}`));
  };

  const columns: ColumnsType<TenantInviteCode> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 180,
      render: (code: string) => (
        <Space>
          <code style={{ fontSize: 14, fontWeight: 600 }}>{code}</code>
          <Tooltip title="Copy">
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyCode(code)} />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: TenantInviteCode['status']) => <Tag color={statusColors[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'Intended Recipient',
      key: 'intended',
      render: (_, row) => (
        <div style={{ fontSize: 13 }}>
          {row.intendedCompanyName && <div style={{ fontWeight: 500 }}>{row.intendedCompanyName}</div>}
          {row.intendedContactEmail && <div style={{ color: '#666', fontSize: 12 }}>{row.intendedContactEmail}</div>}
          {row.intendedPlanName && <Tag style={{ marginTop: 2 }}>{row.intendedPlanName}</Tag>}
          {!row.intendedCompanyName && !row.intendedContactEmail && !row.intendedPlanName && (
            <span style={{ color: '#bbb' }}>—</span>
          )}
        </div>
      ),
    },
    {
      title: 'Used By',
      key: 'usedBy',
      render: (_, row) => row.usedByCompanyName
        ? (
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 500 }}>{row.usedByCompanyName}</div>
            {row.usedByUserName && <div style={{ color: '#666', fontSize: 12 }}>{row.usedByUserName}</div>}
            {row.usedAt && <div style={{ color: '#999', fontSize: 12 }}>{dateTimeFormat.format(new Date(row.usedAt))}</div>}
          </div>
        )
        : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 160,
      render: (v: string | null) => v ? dateTimeFormat.format(new Date(v)) : '—',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (n: string | null) => n || <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Created',
      key: 'created',
      width: 160,
      render: (_, row) => (
        <div style={{ fontSize: 12 }}>
          <div>{dateTimeFormat.format(new Date(row.createdAt))}</div>
          {row.createdByName && <div style={{ color: '#999' }}>by {row.createdByName}</div>}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, row) =>
        row.status === 'active' ? (
          <Popconfirm title="Revoke this invite code?" onConfirm={() => handleRevoke(row.id)} okText="Revoke" okButtonProps={{ danger: true }}>
            <Button type="text" danger size="small" icon={<StopOutlined />}>Revoke</Button>
          </Popconfirm>
        ) : null,
    },
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setError(null)} />}

      <div style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          message="Tenant Invite Codes"
          description="These platform-issued codes authorize a new trucking company to register on the platform. Each code is single-use; once redeemed, it is permanently bound to the created tenant for audit."
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ color: '#999', fontSize: 14 }}>{total} tenant invite codes</span>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Generate Invite Code</Button>
      </div>

      {codes.length === 0 ? (
        <Card>
          <Empty image={<KeyOutlined style={{ fontSize: 48, color: '#ccc' }} />} description="No tenant invite codes yet. Generate one to let a new company register." />
        </Card>
      ) : (
        <Card size="small">
          <Table<TenantInviteCode>
            columns={columns}
            dataSource={codes}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />
        </Card>
      )}

      <Modal
        title="Generate Tenant Invite Code"
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="Generate"
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="expiresInHours"
            label="Expires In (hours)"
            rules={[{ required: true, message: 'Expiration is required' }, { type: 'number', min: 1, max: 8760 }]}
            extra="Default: 168 hours (7 days). Max: 8760 (1 year)."
          >
            <InputNumber style={{ width: '100%' }} min={1} max={8760} />
          </Form.Item>
          <Form.Item name="intendedCompanyName" label="Intended Company (optional)" extra="Pre-fill who this code is for, e.g. 'ACME Trucking Co'.">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="intendedContactEmail" label="Intended Contact Email (optional)" rules={[{ type: 'email', message: 'Invalid email' }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="intendedPlanId" label="Assign Plan (optional)" extra="Reference only at this time; does not force plan on registration.">
            <Select
              allowClear
              placeholder="No plan preference"
              options={plans.filter(p => p.isActive).map(p => ({
                label: `${p.name} ($${p.basePrice}/mo)`,
                value: p.id,
              }))}
              getPopupContainer={(t) => t.closest('.ant-modal-body') as HTMLElement || document.body}
            />
          </Form.Item>
          <Form.Item name="note" label="Internal Note (optional)" extra="Visible only to platform admins.">
            <TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminTenantInviteCodesPage;
